import { ReactNode, useEffect, useLayoutEffect, useState } from 'react';
import useResizeObserver from 'use-resize-observer';
import { ChipLineRoot, ChipSlot } from './mobile-card-list.elements';

export interface ChipLineItem {
  key: string;
  node: ReactNode;
  /** Counts toward the used width but is never skipped, e.g. the urgency glyph. */
  isPinned?: boolean;
}

// sub-pixel rounding of chip widths must not drop a chip that visually fits
const FIT_TOLERANCE_PX = 0.5;

const NO_HIDDEN_KEYS: string[] = [];

const useFontsLoaded = () => {
  const [areFontsLoaded, setAreFontsLoaded] = useState(
    () => document.fonts.status === 'loaded',
  );

  useEffect(() => {
    if (areFontsLoaded) return;

    let isCancelled = false;
    const waitForFonts = async () => {
      await document.fonts.ready;
      if (!isCancelled) setAreFontsLoaded(true);
    };
    void waitForFonts();

    return () => {
      isCancelled = true;
    };
  }, [areFontsLoaded]);

  return areFontsLoaded;
};

/**
 * One nowrap line of chips, fitted in Display order: a chip that does not fit is skipped and
 * the following, possibly shorter, chips still get their chance. There is no "+N" element.
 *
 * Skipped chips stay in the DOM out of flow, so they keep a measurable width for the next fit
 * pass; a `display: none` chip would measure 0 and never come back.
 */
export const CardChipLine = ({
  items,
  isFirstLine = false,
}: {
  items: ChipLineItem[];
  /** The line is the card's first line, beside the time, with no title above it. */
  isFirstLine?: boolean;
}) => {
  const [line, setLine] = useState<HTMLDivElement | null>(null);
  const [hiddenKeys, setHiddenKeys] = useState<string[]>(NO_HIDDEN_KEYS);
  const areFontsLoaded = useFontsLoaded();

  const { width: lineWidth } = useResizeObserver({ ref: line });

  // useLayoutEffect: the fit has to land before paint, or a card the virtualizer mounts during
  // scroll flashes its full clipped line for a frame. useEffect runs after that paint, and
  // there is no DOM to measure any earlier.
  useLayoutEffect(() => {
    if (!line) return;

    const available = line.clientWidth + FIT_TOLERANCE_PX;
    const gap = parseFloat(getComputedStyle(line).columnGap) || 0;
    const pinnedKeys = new Set(items.filter((item) => item.isPinned).map((item) => item.key));
    const nextHiddenKeys: string[] = [];
    let used = 0;

    for (const chip of Array.from(line.children)) {
      if (!(chip instanceof HTMLElement)) continue;

      const needed = (used > 0 ? gap : 0) + chip.getBoundingClientRect().width;

      if (pinnedKeys.has(chip.dataset.chipKey ?? '') || used + needed <= available) {
        used += needed;
      } else {
        nextHiddenKeys.push(chip.dataset.chipKey ?? '');
      }
    }

    setHiddenKeys((previous) =>
      previous.join('|') === nextHiddenKeys.join('|') ? previous : nextHiddenKeys,
    );
  }, [line, items, lineWidth, areFontsLoaded]);

  return (
    <ChipLineRoot ref={setLine} isFirstLine={isFirstLine}>
      {items.map((item) => (
        <ChipSlot
          key={item.key}
          data-chip-key={item.key}
          isHidden={hiddenKeys.includes(item.key)}
        >
          {item.node}
        </ChipSlot>
      ))}
    </ChipLineRoot>
  );
};
